import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('ftasm_token'));

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const storedToken = localStorage.getItem('ftasm_token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` },
        withCredentials: true
      });
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      localStorage.removeItem('ftasm_token');
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    const { token: newToken, ...userData } = response.data;
    localStorage.setItem('ftasm_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, phone) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password, phone }, { withCredentials: true });
    const { token: newToken, ...userData } = response.data;
    localStorage.setItem('ftasm_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const staffLogin = async (sessionId) => {
    const response = await axios.post(`${API}/auth/staff/session`, { session_id: sessionId }, { withCredentials: true });
    const { token: newToken, ...userData } = response.data;
    localStorage.setItem('ftasm_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const staffLoginWithCredentials = async (email, password) => {
    const response = await axios.post(`${API}/auth/staff/login`, { email, password }, { withCredentials: true });
    const { token: newToken, ...userData } = response.data;
    localStorage.setItem('ftasm_token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const setAuthData = (userData) => {
    if (userData && userData.token) {
      localStorage.setItem('ftasm_token', userData.token);
      setToken(userData.token);
      const { token: _, ...rest } = userData;
      setUser(rest);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch (e) {
      // Ignore errors
    }
    localStorage.removeItem('ftasm_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, staffLogin, staffLoginWithCredentials, setAuthData, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
